import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Box, Button, Typography } from "@mui/material";

import { colors } from "../../../theme/palette";
import HeaderBG from "../../../assets/images/profiles/entity-profile-bg.png";
import { UserInfo } from "../../../types";
import { followUser, unFollowUser } from "../../../api/network";
import { useDataFormatter } from "../../../hooks/useDataFormatter";
import { EntityHeaderSkeleton } from "../../../pages/Entity/Components/EntityHeader";

const EntityHeader = ({ entityInfo, isLoading }: { entityInfo?: UserInfo; isLoading: boolean }) => {
  const { fullName, firstTwoChr, media, followersCount, role, isFollowing, id, countryName, cityName } =
    useDataFormatter(entityInfo);

  const [isUserFollowing, setIsUserFollowing] = useState<boolean | undefined>(isFollowing);

  useEffect(() => {
    setIsUserFollowing(isFollowing);
  }, [isFollowing]);

  const { t } = useTranslation();

  if (isLoading || !entityInfo) return <EntityHeaderSkeleton />;

  return (
    <Box
      sx={{
        height: "fit-content",
        borderRadius: "8px",
        border: "1px solid #EAECF0",
        background: colors.White,
        position: "relative",
      }}
    >
      <Box
        sx={{
          height: "145px",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          backgroundImage: `url(${HeaderBG})`,
        }}
      />

      <Box
        sx={{
          display: "flex",
          width: "100%",
          justifyContent: "space-between",
          padding: "15px 17px 27px",
        }}
      >
        <Box
          sx={{
            width: "fit-content",
            position: "relative",
            paddingBottom: "10px",
          }}
        >
          <Avatar
            sx={{
              width: "110px",
              height: "110px",
              position: "absolute",
              top: "-55px",
            }}
            src={media}
            alt={fullName}
          >
            {firstTwoChr}
          </Avatar>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "7px",
              paddingInlineStart: "150px",
            }}
          >
            <Typography variant="body16Bold" sx={{ fontWeight: "700" }}>
              {fullName}
            </Typography>
            <Typography
              variant="body14Bold"
              sx={{
                fontWeight: "600",
                color: colors.Dark600,
              }}
            >
              {role}
              {cityName && ` . ${cityName}`}
              {countryName && ` . ${countryName}`}
              {Number(followersCount) ? ` . ${followersCount} ${t("pages.entity.followers")}` : ""}
            </Typography>
          </Box>
        </Box>

        {isUserFollowing ? (
          <Button
            variant="secondary-md"
            sx={{
              height: "fit-content",

              fontFamily: "HeadingNowVarTrial",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "20px",
              minWidth: "110px",
            }}
            onClick={async () => {
              if (!id) return;
              const isFollowing = await unFollowUser(id);
              if (!isFollowing) setIsUserFollowing((prevIsUserFollowing) => !prevIsUserFollowing);
            }}
          >
            {t("pages.entity.unfollow")}
          </Button>
        ) : (
          <Button
            variant="primary-sm"
            sx={{
              height: "fit-content",

              fontFamily: "HeadingNowVarTrial",
              fontSize: "14px",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "20px",
              minWidth: "110px",
            }}
            onClick={async () => {
              if (!id) return;
              const isFollowing = await followUser(id);
              if (isFollowing) setIsUserFollowing((prevIsUserFollowing) => !prevIsUserFollowing);
            }}
          >
            {t("pages.entity.follow")}
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default EntityHeader;
